import { useEffect, useRef } from 'react';
import { api } from './api';
import { useWorkflows } from '@/store/workflows';
import type { NodeKey, Upload } from './types';

const POLL_MS = 2000;

export function StreamPollerEffect() {
  const workflows = useWorkflows((s) => s.workflows);
  const setNodeState = useWorkflows((s) => s.setNodeState);
  const pushUpload = useWorkflows((s) => s.pushUpload);
  const setPendingReview = useWorkflows((s) => s.setPendingReview);
  const pushToast = useWorkflows((s) => s.pushToast);

  const seen = useRef<Record<string, { uploadUrls: Set<string>; lastSpike: string | null; lastRawCount: number; pendingClipId: string | null }>>({});

  useEffect(() => {
    let cancelled = false;
    const ids: number[] = [];

    for (const wf of workflows) {
      if (!wf.tracking || !wf.streamerHandle) continue;
      const name = wf.streamerHandle;
      if (!seen.current[wf.id]) {
        seen.current[wf.id] = { uploadUrls: new Set(), lastSpike: null, lastRawCount: 0, pendingClipId: null };
      }

      const tick = async () => {
        try {
          const [state, clips] = await Promise.all([
            api.getStreamer(name),
            api.listClips(name).catch(() => ({ raw: [], edited: [] }))
          ]);
          if (cancelled) return;
          const mem = seen.current[wf.id];

          const liveNow = state.live[state.live.length - 1]?.is_live === true;
          setNodeState(wf.id, 'source', liveNow
            ? { kind: 'active', label: 'tracking · live' }
            : { kind: 'active', label: 'tracking' });

          const lastBucket = state.buckets[state.buckets.length - 1] ?? null;
          const spikeOn = !!lastBucket?.is_spike;
          setNodeState(wf.id, 'spike', spikeOn
            ? { kind: 'active', label: lastBucket ? `rate ${Math.round(lastBucket.rate)}` : 'spike' }
            : { kind: 'idle' });
          if (spikeOn && lastBucket && mem.lastSpike !== lastBucket.ts) {
            mem.lastSpike = lastBucket.ts;
            pushToast('chat spike → recording', wf.id);
          }

          const rawCount = clips.raw.length;
          if (rawCount > mem.lastRawCount) {
            const delta = rawCount - mem.lastRawCount;
            mem.lastRawCount = rawCount;
            setNodeState(wf.id, 'finalized', { kind: 'flash' });
            window.setTimeout(() => setNodeState(wf.id, 'finalized', { kind: 'idle' }), 800);
            pushToast(`clip saved (${delta} new)`, wf.id);
          }

          const editedBase = new Set(clips.edited.map((f) => f.name.replace(/\.edited\.[^.]+$/, '')));
          const rawPending = clips.raw.filter((f) => !editedBase.has(f.name.replace(/\.[^.]+$/, '')));
          setNodeState(wf.id, 'edit', rawPending.length > 0
            ? { kind: 'active', label: `editing ${rawPending.length}` }
            : { kind: 'idle' });

          const uploadedBasenames = new Set(state.uploads.map((u) => u.edited_basename ?? ''));
          const awaiting = clips.edited.find((f) => !uploadedBasenames.has(f.name));
          if (awaiting) {
            if (!wf.autoUpload && mem.pendingClipId !== awaiting.name) {
              mem.pendingClipId = awaiting.name;
              setPendingReview(wf.id, {
                clipId: awaiting.name,
                clipUrl: `/api/streamers/${encodeURIComponent(name)}/file/edited/${encodeURIComponent(awaiting.name)}`,
                title: `${name} clip · ${new Date(awaiting.mtime).toLocaleString()}`,
                description: wf.editPrompt || ''
              });
              setNodeState(wf.id, 'gate', { kind: 'active', label: 'awaiting review' });
            } else if (wf.autoUpload) {
              setNodeState(wf.id, 'gate', { kind: 'active', label: 'auto-publishing' });
              api.triggerUpload(name, awaiting.name).catch(() => {});
            }
          } else {
            setNodeState(wf.id, 'gate', { kind: 'idle' });
          }

          for (const u of state.uploads) {
            if (!u.url || mem.uploadUrls.has(u.url)) continue;
            mem.uploadUrls.add(u.url);
            const upload: Upload = {
              platform: u.platform,
              url: u.url,
              title: u.title ?? '',
              createdAt: Date.parse(u.ts) || Date.now()
            };
            pushUpload(wf.id, upload);
            pushToast(`published to ${u.platform}`, wf.id);
          }
          setNodeState(wf.id, 'output', state.uploads.length > 0 ? { kind: 'done' } : { kind: 'idle' });
        } catch {
          (['spike', 'finalized', 'edit', 'gate'] as NodeKey[]).forEach((k) =>
            setNodeState(wf.id, k, { kind: 'idle' })
          );
        }
      };

      void tick();
      ids.push(window.setInterval(tick, POLL_MS));
    }

    return () => {
      cancelled = true;
      ids.forEach((id) => window.clearInterval(id));
    };
  }, [workflows.map((w) => `${w.id}:${w.tracking}:${w.streamerHandle}:${w.autoUpload}`).join('|')]);

  return null;
}
